import type { FormikErrors, FormikHelpers, FormikValues } from 'formik';
import { Formik } from 'formik';
import type { FC, ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { boolean, object, string } from 'yup';
import ScrollToError from './components/ScrollToError';
import { ERROR_MESSAGES, REGEX } from './constants';
import { getSchemaFromField } from './utils';
import Button from '~/components/actions/Button';
import config from '~/config';
import AusPostCode from '~/components/inputs/AusPostcode';
import Checkbox from '~/components/inputs/Checkbox';
import DateOfBirth from '~/components/inputs/DateOfBirth';
import File from '~/components/inputs/File';
import Heading from '~/components/inputs/Heading';
import ImageRadioGroup from '~/components/inputs/ImageRadioGroup';
import RadioGroup from '~/components/inputs/RadioGroup';
import Region from '~/components/inputs/Region';
import Select from '~/components/inputs/Select';
import SimpleInput from '~/components/inputs/SimpleInput';
import Textarea from '~/components/inputs/Textarea';
import TFAInput from '~/components/inputs/TFAInput';
import { useReCAPTCHAComponent } from '~/hooks/useReCAPTCHAComponent';
import type { FormInput } from '~/types/inputs';
import type { Dict } from '~/types/utils';
import { scrollElementIntoView } from '~/utils/dom';
import Lookup from '~/components/inputs/Lookup';
import Dealer from '~/components/inputs/Dealer';
import './style.css';

const Form: FC<{
	inputs: FormInput[];
	onSubmit: (
		values: Dict<string | boolean>,
		recaptchaToken: string,
		setErrors: (errors: FormikErrors<any>) => void,
	) => Promise<any>;
	children?: ReactNode;
	buttonChildren?: ReactNode;
	privacyPolicyRedirect?: string;
	termsRedirect?: string;
	minimumAge?: number;
	promoSlug?: string;
}> = ({
	inputs,
	children,
	onSubmit,
	buttonChildren = 'Submit',
	privacyPolicyRedirect = '',
	termsRedirect = '',
	minimumAge,
	promoSlug,
}) => {
	const [submissionError, setSubmissionError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const captchaRef = useRef<any>();
	const ReCAPTCHA = useReCAPTCHAComponent();

	const fetchToken = (): Promise<string> =>
		new Promise((resolve, reject) => {
			const hoistPetard = () => {
				const error = new Error(
					'Failed to fetch recaptcha token. Please refresh the page and try again.',
				);
				reject(error);
			};
			const timeout = setTimeout(hoistPetard, 10000);

			captchaRef.current
				.executeAsync()
				.then((token: string) => {
					clearTimeout(timeout);
					resolve(token);
				})
				.catch(hoistPetard);
		});

	const formSchema = useMemo(
		() =>
			object().shape(
				inputs.reduce((acc, curr) => {
					let baseSchema = {
						...acc,
						[curr.name]: getSchemaFromField(curr),
					};

					if (curr.name === 'aus_postcode') {
						baseSchema = {
							...baseSchema,
							'aus_postcode:manualMode': boolean(),
							suburb: string().when('aus_postcode:manualMode', {
								is: true,
								then: string().required(ERROR_MESSAGES.suburb.REQUIRED),
							}),
							postcode: string().when('aus_postcode:manualMode', {
								is: true,
								then: string()
									.required(ERROR_MESSAGES.postcode.REQUIRED)
									.length(4, ERROR_MESSAGES.postcode.INVALID_FORMAT)
									.matches(
										REGEX.AUS_POSTCODE,
										ERROR_MESSAGES.postcode.INVALID_FORMAT,
									),
							}),
							state: string().when('aus_postcode:manualMode', {
								is: true,
								then: string().required(ERROR_MESSAGES.state.REQUIRED),
							}),
						};
					}

					if (curr.name === 'dealer') {
						baseSchema = {
							...baseSchema,
							'dealer:manualMode': boolean(),
							dealer: string().when('dealer:manualMode', {
								is: true,
								then: string().optional(),
							}),
							dealer_other: string().when('dealer:manualMode', {
								is: true,
								then: string().required('Please enter your dealer name'),
							}),
						};
					}

					if (curr.name === 'date_of_birth' && minimumAge) {
						baseSchema = {
							...baseSchema,
							date_of_birth: string()
								.required(ERROR_MESSAGES.date_of_birth.REQUIRED)
								.test({
									name: 'age',
									message: ERROR_MESSAGES.date_of_birth.INVALID_AGE(minimumAge),
									test: value => {
										const date = new Date(value);
										const now = new Date();
										const age = now.getFullYear() - date.getFullYear();
										const month = now.getMonth() - date.getMonth();

										if (
											month < 0 ||
											(month === 0 && now.getDate() < date.getDate())
										) {
											return age - 1 >= minimumAge;
										}

										return age >= minimumAge;
									},
								}),
						};
					}

					return baseSchema;
				}, {}),
			),
		[inputs],
	);

	const initialValues = useMemo(() => {
		return inputs.reduce((acc, curr) => {
			if (curr.type === 'checkbox') {
				return {
					...acc,
					[curr.name]: 'false',
				};
			}

			if (curr.name === 'dealer') {
				return {
					...acc,
					[curr.name]: '',
					'dealer:manualMode': false,
					dealer_other: '',
				};
			}

			if (curr.name === 'aus_postcode') {
				return {
					...acc,
					'aus_postcode:manualMode': false,
					[curr.name]: '',
					suburb: '',
					postcode: '',
					state: '',
				};
			}

			return {
				...acc,
				[curr.name]: '',
			};
		}, {});
	}, []);
	const inputComponents = useMemo(() => {
		return inputs.map(input => {
			switch (input.type) {
				case 'text':
				case 'tel':
				case 'email':
				case 'date':
				case 'number':
					return <SimpleInput key={input.name} {...input} />;
				case 'checkbox':
					return (
						<Checkbox
							key={input.name}
							termsRedirect={termsRedirect}
							privacyPolicyRedirect={privacyPolicyRedirect}
							{...input}
						/>
					);
				case 'file':
					return <File key={input.name} {...input} promoSlug={promoSlug} />;
				case 'textarea':
					return <Textarea key={input.name} {...input} />;
				case 'select':
					return <Select key={input.name} {...input} />;
				case 'tfa':
					return <TFAInput key={input.name} {...input} />;
				case 'region':
					return <Region key={input.name} {...input} />;
				case 'dob':
					return <DateOfBirth key={input.name} {...input} />;
				case 'aus_postcode':
					return <AusPostCode key={input.name} promoSlug={promoSlug} {...input} />;
				case 'image_radio_group':
					return <ImageRadioGroup key={input.name} {...input} />;
				case 'radio':
					return <RadioGroup key={input.name} {...input} />;
				case 'heading':
					return <Heading key={input.name} {...input} />;
				case 'lookup':
					return <Lookup key={input.name} {...input} />;
				case 'dealer':
					return <Dealer key={input.name} {...input} />;
				default:
					return null;
			}
		});
	}, [inputs]);

	const handleSuccessSubmit = async (
		values: FormikValues,
		helpers: FormikHelpers<FormikValues>,
	) => {
		/**
		 * Submission flow:
		 * 1. Client side validation
		 * 2. Submit to API
		 *     - If API returns errors, highlight them in UI
		 *     - Allow user to correct the errors and re-submit
		 * 4. Redirect user to result page
		 */
		setIsSubmitting(true);
		setSubmissionError('');

		let recaptchaToken;

		try {
			recaptchaToken = await fetchToken();
		} catch (e: any) {
			if (e.message) {
				setSubmissionError(e.message);
			}
			scrollElementIntoView(document.getElementById('submission_error'), 'center');
		}

		try {
			if (!recaptchaToken) {
				setIsSubmitting(false);
				return;
			}

			const cleanValues = Object.entries(values).reduce<FormikValues>(
				(acc, [key, currValue]) => {
					// When we're manipulating the suburb/state/postcode values
					// we can't guarantee the order of the values,
					// So we have to make sure we don't overwrite our desired values
					const value = acc[key] || currValue;

					if (key === 'mobile') {
						return {
							...acc,
							[key]: value.replace(/[\s-]/g, ''),
						};
					}

					/**
					 * If the user has done a manual postcode/state entry,
					 * we need to compose it in to the same stringified value structure
					 * as a regular lookup. If they have done a regular lookup we
					 * need to explode the values out and set them as separate fields.
					 */

					if (key === 'aus_postcode') {
						if (values['aus_postcode:manualMode']) {
							return {
								...acc,
								[key]: `${values.suburb || ''}, ${values.postcode || ''}, ${
									values.state || ''
								}`,
							};
						} else {
							const [suburb, postcode, state] = value.split(', ');

							return {
								...acc,
								[key]: value,
								suburb: suburb,
								postcode: postcode,
								state: state,
							};
						}
					}

					if (typeof value === 'string') {
						return {
							...acc,
							[key]: value.trim(),
						};
					}

					return {
						...acc,
						[key]: value,
					};
				},
				{},
			);

			await onSubmit(cleanValues, recaptchaToken, helpers.setErrors);
		} catch (e) {
			setSubmissionError('Failed to submit. Please check the form for errors and try again.');
			scrollElementIntoView(document.getElementById('submission_error'), 'start');
		}
		setIsSubmitting(false);
	};

	return (
		<Formik
			onSubmit={handleSuccessSubmit}
			validationSchema={formSchema}
			validateOnBlur={false}
			initialValues={initialValues}
		>
			{({ handleSubmit, handleReset }) => {
				return (
					<form
						onSubmit={handleSubmit}
						onReset={handleReset}
						className="w-full mt-8"
						noValidate
					>
						<div id="submission_error">
							{submissionError && (
								<div className="form-submission-error rounded-lg p-4 text-left font-large-body mb-6">
									{submissionError}
								</div>
							)}
						</div>
						<ScrollToError />
						{ReCAPTCHA && (
							<ReCAPTCHA
								ref={captchaRef}
								sitekey={config.recaptchaSiteKey}
								size="invisible"
								className="invisible absolute"
							/>
						)}
						<div className="grid grid-cols-6 gap-6 mb-6 mx-auto">
							{inputComponents}
							<div className="col-span-6 font-small-body text-left">
								<p>* Denotes Mandatory Field</p>
								{children}
							</div>
						</div>
						<Button type="submit" isFullWidth isSubmitting={isSubmitting}>
							{buttonChildren}
						</Button>
						{children}
					</form>
				);
			}}
		</Formik>
	);
};

export default Form;
